import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageOff, Film, Upload, RotateCcw } from "lucide-react";
import { api, fetchFileBlob, newClientId, parseError } from "../lib/api";
import { Btn, cx, StatusChip } from "./kit";
import { useAuth } from "../lib/auth";

let configCache = null;
export function useMediaConfig() {
  const [cfg, setCfg] = useState(configCache);
  useEffect(() => {
    if (!configCache) api.get("/config").then((r) => { configCache = r.data; setCfg(r.data); });
  }, []);
  return cfg;
}

export function AuthImage({ fileId, alt, className = "", contain, testid }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let u;
    fetchFileBlob(fileId).then((x) => { u = x; setUrl(x); }).catch(() => setFailed(true));
    return () => u && URL.revokeObjectURL(u);
  }, [fileId]);
  if (failed) return <div className={cx("flex items-center justify-center bg-muted", className)}><ImageOff className="h-6 w-6 text-muted-foreground" /></div>;
  if (!url) return <div className={cx("animate-pulse bg-muted", className)} />;
  return <img data-testid={testid} src={url} alt={alt} className={cx(contain ? "object-contain" : "object-cover", "bg-muted", className)} />;
}

export function FileThumb({ file, onOpen }) {
  const { t } = useTranslation();
  return (
    <button onClick={() => onOpen?.(file)} data-testid={`file-thumb-${file.id}`} className="group block w-full overflow-hidden rounded-xl border bg-white text-left lift">
      {file.kind === "video" ? (
        <div className="flex aspect-square items-center justify-center bg-muted"><Film className="h-8 w-8 text-muted-foreground" /></div>
      ) : (
        <AuthImage fileId={file.id} alt={file.title} className="aspect-square w-full" />
      )}
      <div className="p-2 text-xs">
        <p className="truncate font-semibold">{file.title}</p>
        <p className="truncate text-muted-foreground">{file.author_name || ""}{file.region ? ` · ${t(`region.${file.region}`)}` : ""}</p>
      </div>
    </button>
  );
}

function DemoGallery({ dogId, onAdded, meta, testid }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [urls, setUrls] = useState({});
  const [done, setDone] = useState({});
  useEffect(() => {
    api.get("/demo/gallery").then(async (r) => {
      setItems(r.data);
      for (const g of r.data) {
        const b = await api.get(`/demo/gallery/${g.id}/content`, { responseType: "blob" });
        setUrls((u) => ({ ...u, [g.id]: URL.createObjectURL(b.data) }));
      }
    });
  }, []);
  const attach = async (g) => {
    const { data } = await api.post(`/demo/gallery/${g.id}/attach`, { dog_id: dogId, client_id: newClientId(), region: meta.region || null, source: meta.source || "groomer_observation" });
    setDone((d) => ({ ...d, [g.id]: true }));
    onAdded?.(data);
  };
  return (
    <div data-testid={testid} className="space-y-2">
      <p data-testid="demo-upload-disabled" className="rounded-lg bg-amber-soft p-2 text-xs">{t("sandbox.upload_disabled")}</p>
      <p className="text-xs font-semibold">{t("sandbox.gallery")}</p>
      <div className="grid grid-cols-3 gap-2">
        {items.map((g) => (
          <button key={g.id} type="button" data-testid={`demo-gallery-item-${g.file.split(".")[0]}`} onClick={() => attach(g)} aria-label={`${t("sandbox.attach")}: ${g.title}`}
            className={cx("relative overflow-hidden rounded-xl border bg-muted lift", done[g.id] && "ring-2 ring-sage-strong")}>
            {urls[g.id] ? <img src={urls[g.id]} alt={g.title} className="aspect-square w-full object-cover" /> : <div className="aspect-square animate-pulse" />}
            {done[g.id] && <span className="absolute inset-x-0 bottom-0 bg-white/90 text-[10px] font-semibold">{t("sandbox.attached")}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Each queued file keeps its own client id so a retry never creates a duplicate attachment.
export default function FileUploader(props) {
  const { me } = useAuth();
  return me?.user?.sandbox_id ? <DemoGallery {...props} meta={props.meta || {}} testid={props.testid || "file-uploader"} /> : <RealUploader {...props} />;
}

function RealUploader({ dogId, onAdded, meta = {}, testid = "file-uploader", compact }) {
  const { t } = useTranslation();
  const cfg = useMediaConfig();
  const [items, setItems] = useState([]);
  const update = (cid, patch) => setItems((xs) => xs.map((x) => (x.cid === cid ? { ...x, ...patch } : x)));

  const upload = async (item) => {
    update(item.cid, { state: "transferring", error: null });
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("client_id", item.cid);
    fd.append("dog_id", dogId);
    Object.entries(meta).forEach(([k, v]) => v != null && fd.append(k, v));
    try {
      const { data } = await api.post("/files", fd);
      update(item.cid, { state: "added", fileId: data.id });
      onAdded?.(data);
    } catch (e) {
      update(item.cid, { state: "failed", error: parseError(e) });
    }
  };

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const fresh = files.map((f) => ({ cid: newClientId(), file: f, state: "queued" }));
    setItems((xs) => [...xs, ...fresh]);
    fresh.reduce((p, it) => p.then(() => upload(it)), Promise.resolve());
  };

  const accept = cfg ? [...cfg.photo_types, ...cfg.video_types].join(",") : "image/*,video/*";
  return (
    <div data-testid={testid} className="space-y-3">
      {cfg && (
        <p data-testid="upload-limits" className="text-xs text-muted-foreground">
          {t("upload.limits", { photo: cfg.photo_max_mb, video: cfg.video_max_mb })}
        </p>
      )}
      <label className={cx("flex min-h-[48px] cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed bg-white px-4 font-semibold hover:bg-muted", compact ? "py-2" : "py-4")}>
        <Upload className="h-4 w-4" /> {t("upload.choose")}
        <input data-testid={`${testid}-input`} type="file" multiple accept={accept} onChange={pick} className="sr-only" />
      </label>
      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.cid} data-testid={`upload-item-${it.state}`} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-2 text-sm">
              <span className="min-w-0 flex-1 truncate">{it.file.name}</span>
              <StatusChip status={{ queued: "pending", transferring: "started", added: "completed", failed: "stopped" }[it.state]} label={t(`upload.${it.state}`)} testid={`upload-state-${it.state}`} />
              {it.state === "failed" && (
                <>
                  <span className="w-full text-xs text-destructive">{t(`err.${it.error?.code}`, { limit: it.error?.limit_mb })}</span>
                  {it.error?.code !== "file_too_large" && it.error?.code !== "unsupported_type" && (
                    <Btn variant="outline" testid="upload-retry-btn" className="min-h-[36px] px-3 text-sm" onClick={() => upload(it)}><RotateCcw className="h-4 w-4" />{t("upload.retry")}</Btn>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
